import { Component, DestroyRef, inject, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { StateChangedModel, StateService } from "../common/services/state.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SpinnerService } from "../common/services/spinner.service";
import { NotificationService } from "../common/services/notification.service";
import { SignalREventService } from "../common/services/signalR.service";
import { Subscription } from "rxjs";
import * as Sentry from "@sentry/angular";
import { stageRoute } from "../common/constants/stage-routes";
import { Message } from "../common/services/device.service";

@Component({
    template: '',
    standalone: false
})
export abstract class BaseViewComponent implements OnInit, OnDestroy {

    stateService = inject(StateService);
    notificationService = inject(NotificationService);
    signalREventService: SignalREventService = inject(SignalREventService)

    spinnerService = inject(SpinnerService);
    router: Router = inject(Router);

    showLoader = false;

    subscriptions = new Subscription();

    protected readonly destroyRef: DestroyRef = inject(DestroyRef);

    public get ConnectionId(): string | undefined {
        return this.stateService.ConnectionId;
    }

    async ngOnInit(): Promise<void> {
        this.refreshView();
        this.stateService.OnSecureDataCurrentUpdate
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (val) => {
                    if (val) this.onSecureDataChanged(val);
                }
            });
    }

    protected refreshView(): void {

    }

    protected abstract onSecureDataChanged(val: StateChangedModel): void;

    navigate(val: StateChangedModel): void {
        this.navigateToSecureDataType(val.Type);
    }

    navigateToSecureDataType(type: StateChangedModel['Type']): void {
        const route = stageRoute(type);
        if (route) this.navigateByRoute(route);
    }

    /**
     * Error callback for a stage's cancel(). HTTP failures were already reported and shown by
     * HttpErrorInterceptor; anything else (for example no current status) is reported here and shown
     * instead of escaping as an unhandled error while the Cancel button does nothing.
     */
    onCancelError(stageType: string, err: unknown): void {
        if (err instanceof Error && err.message.startsWith('HTTP ')) return;
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
            level: 'warning',
            tags: { secure_data_cancel: stageType, page: this.router.url?.split('?')[0] }
        });
        this.notificationService.showError(Message.ErrorMessage, Message.Error);
    }

    validate() {
        if (!this.ConnectionId) {
            this.notFound();
        }
    }

    notFound() {
        this.router.navigate(['404'], {
            queryParams: this.QueryParams
        });
    }

    navigateCustomerForm() {
        this.router.navigate(['customer-information'], {
            queryParams: this.QueryParams
        });
    }

    navigateCreditForm() {
        this.router.navigate(['credit-information'], {
            queryParams: this.QueryParams
        });
    }

    navigateTermsAndConditionsForm() {
        this.router.navigate(['terms-condition'], {
            queryParams: this.QueryParams
        });
    }

    navigateOrderCheckListForm() {
        this.router.navigate(['order-checklist'], {
            queryParams: this.QueryParams
        });
    }

    navigateNcvcQuestionnaireForm() {
        this.router.navigate(['ncvc-questionnaire'], {
            queryParams: this.QueryParams
        });
    }

    navigateFPVForm() {
        this.router.navigate(['first-party-verification'], {
            queryParams: this.QueryParams
        });
    }

    navigateOutstandingBalanceForm() {
        this.router.navigate(['outstanding-balance'], {
            queryParams: this.QueryParams
        });
    }

    navigatePin() {
        this.router.navigate(['pin'], {
            queryParams: this.QueryParams
        });
    }

    navigateExpired() {
        this.router.navigate(['expired'], {
            queryParams: this.QueryParams
        });
    }

    navigateLocationRequired() {
        return this.router.navigate(['location-required'], {
            queryParams: this.QueryParams
        });
    }

    navigateWaiting(cn = false) {
        this.router.navigate(['waiting'], {
            queryParams: { ...this.QueryParams, cn }
        });
    }

    navigateByRoute(route: string) {
        this.router.navigate([route], {
            queryParams: this.QueryParams
        });
    }

    navigateCustomerAccountSetup() {
        this.router.navigate(['customer-account-setup'], {
            queryParams: this.QueryParams
        });
    }

    navigateWindstreamCustomerAccountSetup() {
        this.router.navigate(['windstream-customer-account-setup'], {
            queryParams: this.QueryParams
        });
    }

    navigatePaymentInformation() {
        this.router.navigate(['payment-information'], {
            queryParams: this.QueryParams
        });
    }

    navigateWirelessAccountVerification() {
        this.router.navigate(['wireless-setup'], {
            queryParams: this.QueryParams
        });
    }

    navigateWirelessAccountAuthentication() {
        this.router.navigate(['wireless-account-authentication'], {
            queryParams: this.QueryParams
        });
    }

    navigateMobileBusinessAgreement() {
        this.router.navigate(['mobile-business-agreement'], {
            queryParams: this.QueryParams
        });
    }

    navigateWirelessSetupUpgarde() {
        this.router.navigate(['wireless-upgrade-payment'], {
            queryParams: this.QueryParams
        });
    }

    private get QueryParams() {
        return {
            id: this.stateService.ConnectionId,
            t: this.stateService.MethodType
        };
    }

    get canLoadContent() {
        return this.stateService.CurrentStatus != undefined;
    }

    sendSignalRAlert(message: string) {
        this.signalREventService.SendEvent({
            UniqueId: this.ConnectionId,
            Type: 'UserAction',
            Data: message
        });
    }

    sendCustomerInputMessage(message: string, setPrefix = true) {
        const data = `${setPrefix ? 'Customer is entering ' : ''}${message}.`;
        this.sendSignalRAlert(data);
    }

    /**
     * Scrolls to the first visible invalid field or error message on the page and focuses it when it is a text input.
     * Runs after change detection so errors shown by markAllAsTouched are rendered. Uses the formcontrolname attribute
     * and the ng-invalid class: ng-reflect-* attributes only exist in dev mode, so production builds never found the field.
     */
    protected scrollToInvalidControl(): void {
        setTimeout(() => {
            const element = Array.from(document.querySelectorAll<HTMLElement>('[formcontrolname].ng-invalid, .p-error'))
                .find(el => el.getClientRects().length > 0);
            if (!element) return;
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = element.matches('input, textarea') ? element : element.querySelector<HTMLElement>('input, textarea');
            // Checkboxes are not focused: focus on them sends customer alerts (FPV) and does not help on mobile.
            if (input && !['checkbox', 'radio'].includes((input as HTMLInputElement).type)) {
                input.focus({ preventScroll: true });
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

}